import {isPlatformBrowser} from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import {Router} from '@angular/router';
import {DcpAuthService} from '@app/auth/services/dcp-auth.service';
import {DcpBaselineAuthService} from '@app/auth/services/dcp-baseline-auth.service';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {SkipToContentConfigModel} from '@app/config/SkipToContentConfig';
import {
  CountrySwitchModalComponent
} from '@app/layout/header/country-selector/country-switch-modal/country-switch-modal.component';
import {DataTrackingService} from '@app/services/analytics/data-tracking.service';
import {CampaignTrackingService} from '@app/services/campaign-tracking.service';
import {DcpContextualNavigationService} from '@app/services/navigation/dcp-contextual-navigation.service';
import {ContextualSearchConfig} from '@app/services/navigation/model/contextual-search-config.model';
import {ContextualUserMenuConfig} from '@app/services/navigation/model/contextual-user-menu-config.model';
import {ShoppingCartConfig} from '@app/services/navigation/model/shopping-cart-config.model';
import {CountrySelectorService} from '@app/services/country-selector.service';
import {CrmAccountTrackingService} from '@app/services/crm-account-tracking.service';
import {Sap4MeHideHeaderService} from '@app/services/sap4me-hide-header.service';
import {FeatureTogglesService, HideHeaderService, SessionStorageService,} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Config, Country, PageMetaService, RoutingService} from '@spartacus/core';
import {HamburgerMenuService, KeyboardFocusService, StorefrontComponent,} from '@spartacus/storefront';
import {Observable, Subject} from 'rxjs';
import {distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {skipToContentAccessibility} from './dcp-storefront-header-accessibility.util';
import {DcpMastheadNavigationService} from '@app/services/navigation/dcp-masthead-navigation.service';
import {ContextualNavigationLink} from '@app/services/navigation/model/contextual-navigation-common.model';

@UntilDestroy()
@Component({
  selector: 'dcp-storefront',
  templateUrl: './dcp-storefront.component.html',
  styleUrls: ['./dcp-storefront.component.scss'],
})
export class DcpStorefrontComponent
  extends StorefrontComponent
  implements OnInit, OnDestroy {
  @Input() isModal = false;
  @ViewChild('countrySwitchModalComponent')
  countrySwitchModalComponentRef: CountrySwitchModalComponent;
  sapChatAdvisorEnabled: boolean = false;
  hideHeader: boolean;
  shoppingCartConfig$: Observable<ShoppingCartConfig>;
  contextualSearchConfig: ContextualSearchConfig =
    this.contextualNavigationService.initialSearchConfig;
  contextualUserMenuConfig$: Observable<ContextualUserMenuConfig>;
  countries: Country[] = [];
  private readonly destroy$ = new Subject<void>();
  isSearchPage: boolean = false;
  contextualNavigationEnabled: boolean = false;
  mastHeadEnabled: boolean = false;

  mainLinks: ContextualNavigationLink[]
  hideExploreSapLink: boolean = true;
  exploreSapLink: ContextualNavigationLink = undefined;

  private hideHeaderService: HideHeaderService = inject(HideHeaderService);
  private pageMetaService: PageMetaService = inject(PageMetaService);

  constructor(
    @Inject(PLATFORM_ID) public platformId: object,
    @Inject(Config) private skipToConfigService: SkipToContentConfigModel,
    public contextualNavigationService: DcpContextualNavigationService,
    public countrySelectorService: CountrySelectorService,
    hamburgerMenuService: HamburgerMenuService,
    private dcpRoutingService: RoutingService,
    elementRef: ElementRef,
    keyboardFocusService: KeyboardFocusService,
    protected sap4meHideHeaderService: Sap4MeHideHeaderService,
    private dataTrackingService: DataTrackingService,
    private campaignTrackingService: CampaignTrackingService,
    private customerIdTrackingService: CrmAccountTrackingService,
    private togglesService: FeatureTogglesService,
    private cdr: ChangeDetectorRef,
    private authService: DcpAuthService,
    private baselineAuthService: DcpBaselineAuthService,
    private sessionStorageService: SessionStorageService,
    protected router: Router,
    protected mastHeadNavigationService: DcpMastheadNavigationService
  ) {
    super(
      hamburgerMenuService,
      dcpRoutingService,
      elementRef,
      keyboardFocusService
    );
  }

  ngOnInit(): void {
    this.embedYoutubeIframeApi();
    this.subscribeToToggle();
    super.ngOnInit();
    this.setupTracking();
    this.displayHeader();
    this.subscribeToCountries();
    this.updateHideHeader(this.hideHeaderService.getHideStatus());
    if (window.self !== window.top) {
      this.isModal = true;
    }
    this.checkCurrentPage();

    this.mastHeadNavigationService.getMainLinks()
      .pipe(
        untilDestroyed(this))
      .subscribe(linksConfig => {
        this.mainLinks = linksConfig.mainLinks as ContextualNavigationLink[];
      });

    this.mastHeadNavigationService.getExploreSap().pipe(
      untilDestroyed(this))
      .subscribe(exploreSapConfig => {
        if (exploreSapConfig.exploreSapLink) {
          this.hideExploreSapLink = false;
          this.exploreSapLink = exploreSapConfig.exploreSapLink;
        }
      });


  }


  @HostBinding('class.pt-0') get headerStatus() {
    return this.hideHeader;
  }

  @HostBinding('class.contextual-navigation') get isContextualNavigation() {
    return !this.isModal && !this.hideHeader;
  }

  @HostBinding('class.contextual-navigation-wrapper') applyWrapper = true;

  displayHeader(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.sap4meHideHeaderService
        .getHideStatus$()
        .pipe(untilDestroyed(this))
        .subscribe((hideHeader) => {
          this.updateHideHeader(hideHeader);
        });
    }
  }

  updateHideHeader(hideHeader: boolean): void {
    this.hideHeader = hideHeader;
    this.elementRef.nativeElement.style.paddingTop = this.hideHeader
      ? '0px'
      : '';
    this.cdr.detectChanges();
  }

  embedYoutubeIframeApi() {
    if (isPlatformBrowser(this.platformId)) {
      const tag: HTMLElementTagNameMap['script'] =
        document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
    }
  }

  setupTracking() {
    if (isPlatformBrowser(this.platformId)) {
      this.dataTrackingService.setupPageHitTracking();
      this.dataTrackingService.setupProductDataTracking();
      this.campaignTrackingService.setupCampaignTracking();
      this.customerIdTrackingService.setUpTrackingForAllParameters();
    }
  }

  subscribeToToggle() {
    this.contextualNavigationService.loadConfig();
    this.shoppingCartConfig$ =
      this.contextualNavigationService.getShoppingCartConfig();
    this.contextualUserMenuConfig$ =
      this.contextualNavigationService.getUserMenuConfig();
    this.contextualNavigationService
      .getContextualSearchConfig()
      .pipe(untilDestroyed(this))
      .subscribe((searchConfig) => {
        this.contextualSearchConfig = searchConfig;
      });

    this.togglesService
      .isEnabled(UNLEASH_TOGGLES.sapChatAdvisor)
      .pipe(untilDestroyed(this))
      .subscribe((isEnabled) => {
        this.sapChatAdvisorEnabled = isEnabled;
      });

    this.togglesService
      .isEnabled(UNLEASH_TOGGLES.contextualNavigationEnabled)
      .pipe(untilDestroyed(this))
      .subscribe((isEnabled) => {
        this.contextualNavigationEnabled = isEnabled;
      });

    this.togglesService
      .isEnabled(UNLEASH_TOGGLES.mastHeadEnabled)
      .pipe(untilDestroyed(this))
      .subscribe((isEnabled) => {
        this.mastHeadEnabled = isEnabled;
      });
  }

  onCountrySelected(isocode: string) {
    if (isocode) {
      this.countrySelectorService.initializeCountrySwitchWarning(
        isocode,
        this.countrySwitchModalComponentRef
      );
    }
  }

  onLogin() {
    this.sessionStorageService.setItem('loginMethod', 'manual');
    this.authService.login();
  }

  onLogout() {
    this.baselineAuthService.logout();
  }

  private subscribeToCountries() {
    this.countrySelectorService
      .getCountries()
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((countries) => {
        this.countries = countries;
      });
  }

  skipToContent(event: FocusEvent) {
    if (this.isSkipToContentActiveForRoute()) {
      if (event.relatedTarget) {
        skipToContentAccessibility(event);
      }
    }
  }

  isSkipToContentActiveForRoute(): boolean {
    let isActive = false;
    this.dcpRoutingService
      .getRouterState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((routerState) => {
        isActive =
          this.skipToConfigService.accessibility?.skipToContent?.paths?.includes(
            routerState.state?.semanticRoute
          );
      });
    return isActive;
  }

  showSapChatAdvisor(): boolean {
    return this.sapChatAdvisorEnabled && this.isSearchPage;
  }

  checkCurrentPage(): void {
    this.pageMetaService.getMeta()
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged()
      )
      .subscribe((meta) => {
        let urlSegments = meta?.canonicalUrl?.split('/') || [];
        urlSegments.forEach(segment => {
          if (segment === 'search') {
            this.isSearchPage = true;
          }
        })
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
